import { useState } from "react";
import api from "../lib/api";
import { Scales, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fmtDays } from "../lib/format";

export default function Judgment() {
  const [jvita, setJvita] = useState(120);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/judgment", { jvita: Number(jvita) });
      setResult(r.data);
    } catch (err) { toast.error("Erreur simulateur"); }
    finally { setLoading(false); }
  };

  const examples = [
    { v: 60, label: "Faible" },
    { v: 120, label: "Préoccupant" },
    { v: 240, label: "Grave" },
    { v: 380, label: "Critique" },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-10">
      <div className="mb-10 fade-up">
        <div className="label-mono">§ 05 · Vita-Judge</div>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl font-medium tracking-tight">Simulateur de jugement</h1>
        <p className="mt-3 text-[var(--muted)] max-w-2xl">Entrez un indicateur synthétique <strong>Jvita</strong> (jours de vie cumulés sacrifiés) pour obtenir la qualification juridique automatique selon la grille vitaliste.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-black/15 border border-black/15">
        <form onSubmit={submit} className="bg-[var(--paper)] p-8" data-testid="judgment-form">
          <div className="label-mono">§ Saisie</div>
          <h2 className="font-serif text-2xl mt-1">Indicateur Jvita</h2>
          <p className="text-sm text-[var(--muted)] mt-2">Jvita = V1 + D2 (jours sacrifiés totaux par an et par citoyen).</p>

          <div className="mt-8">
            <label className="label-mono">Jours de vie sacrifiés (Jvita)</label>
            <input type="range" min={0} max={500} value={jvita} onChange={(e) => setJvita(e.target.value)} className="w-full mt-3 accent-[var(--signal)]" data-testid="judgment-slider" />
            <div className="mt-3 flex items-end gap-2">
              <input type="number" value={jvita} onChange={(e) => setJvita(e.target.value)} className="text-5xl font-light tracking-tighter tabular-nums w-32 border-b-2 border-black bg-transparent focus:outline-none" data-testid="judgment-input" />
              <div className="label-mono pb-2">Jours / an</div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-4 gap-2">
            {examples.map((ex) => (
              <button key={ex.v} type="button" onClick={() => setJvita(ex.v)} data-testid={`judgment-ex-${ex.v}`}
                className="border border-black/30 hover:border-black px-3 py-2 text-xs font-mono">
                {ex.v}j<br/><span className="opacity-60">{ex.label}</span>
              </button>
            ))}
          </div>

          <button type="submit" disabled={loading} data-testid="judgment-submit"
            className="mt-8 w-full bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] py-3 label-mono flex items-center justify-center gap-2 disabled:opacity-60">
            <Scales size={16} weight="bold" /> {loading ? "..." : "Lancer la qualification"} <ArrowRight size={14} weight="bold" />
          </button>

          <div className="mt-8 text-xs font-mono text-[var(--muted)] space-y-1">
            <div>· &lt; 90 j → Satisfaisant</div>
            <div>· 90 — 180 j → Préoccupant</div>
            <div>· 180 — 365 j → Grave</div>
            <div>· ≥ 365 j → Crime contre la vie collective</div>
          </div>
        </form>

        <div className="bg-[var(--paper)] p-8 paper-grain">
          <div className="label-mono">§ Verdict</div>
          {!result ? (
            <div className="mt-4 h-full flex flex-col items-center justify-center text-center text-[var(--muted)]">
              <Scales size={48} weight="duotone" />
              <div className="mt-4 font-serif text-xl">Aucune qualification émise</div>
            </div>
          ) : (
            <div className="mt-4 fade-up" data-testid="judgment-result">
              <div className="border-2 inline-block stamp-rotate px-4 py-2 font-mono text-xs" style={{ borderColor: result.couleur, color: result.couleur }}>
                CODE {result.code}
              </div>
              <div className="mt-6 font-serif text-5xl font-medium" style={{ color: result.couleur }}>
                {result.niveau}
              </div>
              <div className="mt-3 font-mono text-xs text-[var(--muted)]">Jvita évalué = {fmtDays(jvita)}</div>
              <div className="mt-8 label-mono">Qualification</div>
              <p className="mt-2 font-serif text-2xl">{result.qualification}</p>
              <div className="mt-6 label-mono">Conséquences juridiques</div>
              <p className="mt-2 leading-relaxed text-[var(--ink)]">{result.consequence}</p>
              <div className="mt-10 border-t border-black/15 pt-4 font-mono text-xs text-[var(--muted)]">
                Référence : Grille vitaliste de qualification juridique · VITA-LEX 2026
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
